import { checkout, upgrade } from "../api";
import { CONVERSION_PRICE_KEY, SSK_PLAN_CHANGE_PENDING } from "../constants";
import ConfirmUpgradeModal from "@/components/account/subscription/confirm_upgrade_modal.vue";

export default {
  data() {
    return {
      error: "",
      upgradeLoading: false,
    };
  },

  computed: {
    basicPlan() {
      return this.$store.getters.basicPlan;
    },
    trialPlan() {
      return this.$store.getters.trialPlan;
    },
  },

  methods: {
    storeConversionPrice(planText, yearlyToggle, coupon) {
      let currency_value = 0;

      switch (planText) {
        case "lite":
          currency_value = yearlyToggle ? "83.88" : "8.99";
          break;
        case "pro":
          currency_value = yearlyToggle ? "179.88" : "19.99";

          if (coupon === "20DISC") {
            currency_value = "15.99";
          }
          break;
        case "premium":
          currency_value = yearlyToggle ? "419.88" : "49.99";
          break;

        default:
          break;
      }

      localStorage.setItem(CONVERSION_PRICE_KEY, currency_value);
    },

    trackPaidPlanConversion(planText, isDowngrade) {
      const conversion_price = localStorage.getItem(CONVERSION_PRICE_KEY);
      localStorage.removeItem(CONVERSION_PRICE_KEY);

      // Track custom analytics event
      this.$trackEvent(isDowngrade ? "plan_downgraded" : "plan_upgraded", {
        plan: planText,
        conversion_price,
      });

      // Track Google Ads conversion (only for upgrades, not downgrades)
      if (!isDowngrade && typeof gtag === "function") {
        // eslint-disable-next-line no-undef
        gtag("event", "conversion", {
          send_to: "AW-16545029854/0VZ6CN7H8ccZEN69pNE9",
          value: conversion_price,
          currency: "USD",
        });
      }

      // Track Bing purchase event (only for upgrades, not downgrades)
      if (!isDowngrade) {
        window.uetq = window.uetq || [];
        window.uetq.push("event", "purchase", {
          revenue_value: conversion_price,
          currency: "USD",
        });
      }
    },

    upgradeHandler(planText, yearlyToggle = false, coupon) {
      if (
        this.$route.query.coupon &&
        !((this.basicPlan || this.trialPlan) && planText === "lite")
      ) {
        coupon = this.$route.query.coupon;
      }
      // if user is brand new, I.E on the basic plan
      // then we need to create a session to grab users
      // card information. If a user is already on a paid
      // plan and wants to upgrade their plan then we need to call
      // the upgrade endpoint since we already have card details

      // user is on basic plan, create session
      if (this.basicPlan || this.trialPlan) {
        // Store the conversion price so we can report it on the success page.
        this.storeConversionPrice(planText, yearlyToggle, coupon);

        // Track upgrade click
        this.$trackEvent("upgrade_clicked", {
          plan: planText,
          yearly: yearlyToggle,
        });

        this.createSession(planText, yearlyToggle, coupon);

        return;
      }

      // user is on a paid plan, upgrade plan
      return this.upgradeCurrentPaidPlan({ planText, yearlyToggle, coupon });
    },

    async createSession(planText, yearlyToggle, coupon) {
      try {
        this.upgradeLoading = true;

        const checkoutData = {
          user: this.$store.state.user,
          plan: planText,
          yearly: yearlyToggle,
          redirectUrl: `${location.pathname}${location.search}`,
        };

        if (coupon) {
          checkoutData.coupon = coupon;
        }

        const session = await checkout(checkoutData);

        this.upgradeLoading = false;
        this.error = "";
        const { id: userId, plan: currentPlan } = this.$store.state.user;
        if (userId && currentPlan) {
          sessionStorage.setItem(
            SSK_PLAN_CHANGE_PENDING,
            `${userId}|${currentPlan}`
          );
        }
        window.location.href = session.data.url;
      } catch (error) {
        this.upgradeLoading = false;
        this.error = error.response?.data?.error;
      }
    },

    async upgradeCurrentPaidPlan({
      planText,
      yearlyToggle,
      isDowngrade = false,
      coupon,
    }) {
      try {
        this.upgradeLoading = true;
        const upgradeData = {
          plan: planText,
          yearly: yearlyToggle,
        };

        if (coupon) {
          upgradeData.coupon = coupon;
        }

        // Store conversion price for tracking (even for paid upgrades)
        this.storeConversionPrice(planText, yearlyToggle, coupon);

        const { data } = await upgrade(upgradeData);
        const newUserData = {
          plan: data.user.plan,
          stripeCustomerId: data.user.stripeCustomerId,
          stripeSubscriptionId: data.user.stripeSubscriptionId,
        };
        const user = this.$store.state.user;
        const newUser = {
          ...user,
          ...newUserData,
        };
        this.$store.dispatch("setUser", newUser);

        // Track paid plan upgrade/downgrade conversion
        this.trackPaidPlanConversion(planText, isDowngrade);

        const msgKey = isDowngrade
          ? "account.subscription.downgradedSuccessfully"
          : "account.subscription.upgradedSuccessfully";
        this.$showToastSuccess(this.$t(msgKey, { plan: planText }), {
          title: this.$t("common.success"),
        });

        this.upgradeLoading = false;
        this.error = "";

        return true;
      } catch (error) {
        this.upgradeLoading = false;
        this.error = error.response?.data?.error;
      }
    },

    upgradeWithConfirmation({ nextPlan, yearlyToggle }) {
      // If user without plan just upgrade to selected plan
      if (this.basicPlan || this.trialPlan) {
        return this.upgradeHandler(nextPlan.id, yearlyToggle);
      }

      const isYearly = Boolean(yearlyToggle);

      return new Promise((resolve) => {
        this.$egModal.show("confirm_upgrade_modal", {
          component: ConfirmUpgradeModal,
          props: {
            newPlan: nextPlan.id,
            newPlanPrice: `${
              isYearly ? nextPlan.yearlyPrice : nextPlan.monthlyPrice
            }$`,
            isYearly: isYearly,
            proceedWithUpgrade: async () => {
              await this.upgradeHandler(nextPlan.id, yearlyToggle);
              resolve(true);
            },
          },
          on: {
            closed: () => {
              resolve(false);
            },
          },
        });
      });
    },
  },
};
